import React from "react";
import { Col, FormFeedback, Input, Label, Row } from "reactstrap";
import * as Yup from "yup";
import Select from "react-select";
import moment from "moment";
import { FilePond, registerPlugin } from "react-filepond";

import FilePondPluginImageExifOrientation from "filepond-plugin-image-exif-orientation";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";

import { fetchWrapper } from "@helpers/fetchwrapper";

import styles from "@areas/shared/components/personal/modals/styles/customModal.module.css";

registerPlugin(FilePondPluginImageExifOrientation, FilePondPluginImagePreview);

const PROMTOUR_TYPES = [
  { value: 1, label: "Профориентационный тур" },
  { value: 2, label: "День открытых дверей" },
  { value: 3, label: "Экскурсия" },
];

const TARGET_GROUPS = [
  { value: "школьники", label: "Школьники" },
  { value: "студенты", label: "Студенты" },
];

const TRANSLIT_MAP = {
  а: "a",
  б: "b",
  в: "v",
  г: "g",
  д: "d",
  е: "e",
  ё: "e",
  ж: "zh",
  з: "z",
  и: "i",
  й: "y",
  к: "k",
  л: "l",
  м: "m",
  н: "n",
  о: "o",
  п: "p",
  р: "r",
  с: "s",
  т: "t",
  у: "u",
  ф: "f",
  х: "h",
  ц: "ts",
  ч: "ch",
  ш: "sh",
  щ: "sch",
  ъ: "",
  ы: "y",
  ь: "",
  э: "e",
  ю: "yu",
  я: "ya",
};

function slugify(text) {
  if (!text) {
    return "";
  }

  return String(text)
    .toLowerCase()
    .split("")
    .map((char) => TRANSLIT_MAP[char] ?? char)
    .join("")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-+/g, "-");
}

function createUniqueToken() {
  return Math.floor(Date.now() / 1000);
}

function buildSlug(title, companyName, uniqueToken) {
  return [slugify(title), slugify(companyName), uniqueToken]
    .filter((part) => part !== "" && part !== null && part !== undefined)
    .join("-");
}

const initialValues = (promtour, company) => {
  const now = moment().format("DD-MM-YYYY");
  const participantsCount =
    promtour &&
    promtour.ParticipantsCount !== null &&
    promtour.ParticipantsCount !== undefined
      ? promtour.ParticipantsCount
      : "";

  return {
    id: (promtour && promtour.Id) || null,
    title: (promtour && promtour.Title) || "",
    description: (promtour && promtour.Description) || "",
    idType: (promtour && promtour.IdType) || 1,
    date:
      (promtour && promtour.Date && moment(promtour.Date).format("DD-MM-YYYY")) ||
      now,
    address: (promtour && promtour.Address) || "",
    slug: (promtour && promtour.Slug) || "",
    slugUnique: "",
    slugManual: Boolean(promtour && promtour.Slug),
    companyName: (company && company.NameCompany) || "",
    industry: (promtour && promtour.Industry) || "",
    city: (promtour && promtour.City) || "",
    targetGroup: (promtour && promtour.TargetGroup) || "",
    participantsCount,
    contactPerson: (promtour && promtour.ContactPerson) || "",
    image:
      promtour && promtour.Image
        ? `/v1/api/fileManager/getFile?path=${promtour.Image}`
        : null,
    idCompany: (company && company.IdCompany) || 0,
  };
};

const validationSchema = () => {
  return Yup.object({
    title: Yup.string().required("Значение обязательно"),
    description: Yup.string().required("Значение обязательно"),
    idType: Yup.number().required("Значение обязательно"),
    participantsCount: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
          ? null
          : value
      )
      .nullable()
      .integer("Укажите целое число")
      .min(0, "Значение не может быть меньше 0"),
  });
};

const confirm = (values, successFunc) => {
  const data = new FormData();

  data.append("IdCompany", values.idCompany);
  data.append("Title", values.title);
  data.append("Description", values.description);
  data.append("IdType", values.idType);
  data.append("Date", moment(values.date, "DD-MM-YYYY").format("YYYY-MM-DD"));
  data.append("Address", values.address || "");
  data.append("Slug", values.slug || "");
  data.append("Industry", values.industry || "");
  data.append("City", values.city || "");
  data.append("TargetGroup", values.targetGroup || "");
  data.append("ContactPerson", values.contactPerson || "");

  if (
    values.participantsCount !== "" &&
    values.participantsCount !== null &&
    values.participantsCount !== undefined
  ) {
    data.append("ParticipantsCount", Number(values.participantsCount));
  }

  if (values.image && values.image.length > 0 && values.image[0].file) {
    data.append("Image", values.image[0].file);
  }

  if (values.id) {
    data.append("Id", values.id);
    fetchWrapper
      .post(`/v1/api/app/UpdateCompanyPromtour`, data, false)
      .then(() => successFunc());
  } else {
    fetchWrapper
      .post(`/v1/api/app/InsertCompanyPromtour`, data, false)
      .then(() => successFunc());
  }
};

function regenerateSlug(validation, title, uniqueToken) {
  const token = uniqueToken || createUniqueToken();
  validation.setFieldValue("slugUnique", token);
  validation.setFieldValue("slugManual", false);
  validation.setFieldValue(
    "slug",
    buildSlug(title, validation.values.companyName, token)
  );
}

function ModalBodyAdd({ validation }) {
  const isCreate = !validation.values.id;

  const handleTitleChange = (e) => {
    const nextTitle = e.target.value;
    validation.handleChange(e);

    const slugEmpty = !(validation.values.slug || "").trim();
    const shouldGenerate =
      slugEmpty || (isCreate && !validation.values.slugManual);

    if (shouldGenerate) {
      regenerateSlug(
        validation,
        nextTitle,
        validation.values.slugUnique || createUniqueToken()
      );
    }
  };

  const handleSlugChange = (e) => {
    const nextSlug = e.target.value;

    if (!nextSlug.trim() && (validation.values.title || "").trim()) {
      regenerateSlug(validation, validation.values.title, createUniqueToken());
      return;
    }

    validation.setFieldValue("slug", nextSlug);
    validation.setFieldValue("slugManual", true);
  };

  return (
    <Row className="g-2">
      <Input
        type="hidden"
        name="id"
        defaultValue={validation.values.id || null}
      />
      <Input
        type="hidden"
        name="idCompany"
        defaultValue={validation.values.idCompany || 0}
      />
      <Input
        type="hidden"
        name="date"
        defaultValue={validation.values.date || ""}
      />
      <Input
        type="hidden"
        name="companyName"
        defaultValue={validation.values.companyName || ""}
      />
      <Input
        type="hidden"
        name="slugUnique"
        defaultValue={validation.values.slugUnique || ""}
      />
      <Input
        type="hidden"
        name="slugManual"
        defaultValue={validation.values.slugManual ? "1" : ""}
      />
      <Col sm={12}>
        <div>
          <Label htmlFor="promtourTitle-field" className={styles.label}>
            Наименование
          </Label>
          <Input
            name="title"
            id="promtourTitle-field"
            className={`form-control ${styles.input}`}
            placeholder="Введите наименование"
            type="text"
            onChange={handleTitleChange}
            onBlur={validation.handleBlur}
            value={validation.values.title || ""}
            invalid={
              validation.touched.title && validation.errors.title ? true : false
            }
          />
          {validation.touched.title && validation.errors.title ? (
            <FormFeedback type="invalid" className={styles.invalid}>
              {validation.errors.title}
            </FormFeedback>
          ) : null}
        </div>
      </Col>
      <Col sm={12}>
        <div>
          <Label htmlFor="promtourDescription-field" className={styles.label}>
            Описание
          </Label>
          <Input
            name="description"
            id="promtourDescription-field"
            className={`form-control ${styles.input}`}
            placeholder="Введите описание"
            type="textarea"
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.description || ""}
            invalid={
              validation.touched.description && validation.errors.description
                ? true
                : false
            }
          />
          {validation.touched.description && validation.errors.description ? (
            <FormFeedback type="invalid" className={styles.invalid}>
              {validation.errors.description}
            </FormFeedback>
          ) : null}
        </div>
      </Col>
      <Col sm={12}>
        <div>
          <Label htmlFor="promtourType-field" className={styles.label}>
            Тип
          </Label>
          <Select
            name="idType"
            id="promtourType-field"
            options={PROMTOUR_TYPES}
            value={
              validation.values.idType
                ? PROMTOUR_TYPES.find((p) => p.value === validation.values.idType)
                : null
            }
            placeholder="Выберите тип"
            onChange={(e) => {
              validation.setFieldValue("idType", e.value);
            }}
            onBlur={() => validation.setFieldTouched("idType")}
          />
          {validation.touched.idType && validation.errors.idType ? (
            <FormFeedback type="invalid" className={styles.invalid}>
              {validation.errors.idType}
            </FormFeedback>
          ) : null}
        </div>
      </Col>
      <Col sm={12}>
        <div>
          <Label htmlFor="promtourAddress-field" className={styles.label}>
            Адрес
          </Label>
          <Input
            name="address"
            id="promtourAddress-field"
            className={`form-control ${styles.input}`}
            placeholder="Введите адрес"
            type="text"
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.address || ""}
          />
        </div>
      </Col>
      <Col sm={6}>
        <div>
          <Label htmlFor="promtourIndustry-field" className={styles.label}>
            Отрасль
          </Label>
          <Input
            name="industry"
            id="promtourIndustry-field"
            className={`form-control ${styles.input}`}
            placeholder="Введите отрасль"
            type="text"
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.industry || ""}
          />
        </div>
      </Col>
      <Col sm={6}>
        <div>
          <Label htmlFor="promtourCity-field" className={styles.label}>
            Город/район
          </Label>
          <Input
            name="city"
            id="promtourCity-field"
            className={`form-control ${styles.input}`}
            placeholder="Введите город или район"
            type="text"
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.city || ""}
          />
        </div>
      </Col>
      <Col sm={6}>
        <div>
          <Label htmlFor="promtourTargetGroup-field" className={styles.label}>
            Целевая группа
          </Label>
          <Select
            name="targetGroup"
            id="promtourTargetGroup-field"
            options={TARGET_GROUPS}
            isClearable
            value={
              validation.values.targetGroup
                ? TARGET_GROUPS.find(
                    (g) => g.value === validation.values.targetGroup
                  ) || null
                : null
            }
            placeholder="Выберите целевую группу"
            onChange={(e) => {
              validation.setFieldValue("targetGroup", e ? e.value : "");
            }}
            onBlur={() => validation.setFieldTouched("targetGroup")}
          />
        </div>
      </Col>
      <Col sm={6}>
        <div>
          <Label
            htmlFor="promtourParticipantsCount-field"
            className={styles.label}
          >
            Количество участников
          </Label>
          <Input
            name="participantsCount"
            id="promtourParticipantsCount-field"
            className={`form-control ${styles.input}`}
            placeholder="Введите количество"
            type="number"
            min={0}
            step={1}
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={
              validation.values.participantsCount === null ||
              validation.values.participantsCount === undefined
                ? ""
                : validation.values.participantsCount
            }
            invalid={
              validation.touched.participantsCount &&
              validation.errors.participantsCount
                ? true
                : false
            }
          />
          {validation.touched.participantsCount &&
          validation.errors.participantsCount ? (
            <FormFeedback type="invalid" className={styles.invalid}>
              {validation.errors.participantsCount}
            </FormFeedback>
          ) : null}
        </div>
      </Col>
      <Col sm={12}>
        <div>
          <Label htmlFor="promtourContactPerson-field" className={styles.label}>
            Контактное лицо
          </Label>
          <Input
            name="contactPerson"
            id="promtourContactPerson-field"
            className={`form-control ${styles.input}`}
            placeholder="Введите контактное лицо"
            type="text"
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.contactPerson || ""}
          />
        </div>
      </Col>
      <Col sm={12}>
        <div>
          <Label htmlFor="promtourSlug-field" className={styles.label}>
            Slug
          </Label>
          <Input
            name="slug"
            id="promtourSlug-field"
            className={`form-control ${styles.input}`}
            placeholder="URL-идентификатор"
            type="text"
            onChange={handleSlugChange}
            onBlur={validation.handleBlur}
            value={validation.values.slug || ""}
          />
        </div>
      </Col>
      <Col sm={12}>
        <FilePond
          files={validation.values.image || null}
          onupdatefiles={(e) => {
            validation.setFieldValue("image", e);
          }}
          name="image"
          allowMultiple={false}
          className="filepond filepond-input-multiple"
          labelIdle="Перетащите и нажмите, чтобы загрузить"
          onaddfile={() => validation.setFieldTouched("image")}
        />
      </Col>
    </Row>
  );
}

export { initialValues, validationSchema, confirm, ModalBodyAdd, PROMTOUR_TYPES };
